import { useState } from "react";
import { Button } from "./ui/button/button";
import { ButtonSize, ButtonType } from "./ui/button/button.type";
import { Drawer, DrawerContent } from "./ui/drawer";
import { orderProcessorContract, useSmartWallet } from "@/contexts/SmartWallet";
import { toast } from "./ui/use-toast";
import { useSendTransaction } from "thirdweb/react";
import { customPrepareContractCall } from "@/helper/thirdweb";
import { useOrders } from "@/contexts";
import { trackAmplitudeEvent } from "@/utils/amplitude";

type CancelReasonSheetType = {
  toggleSheet: () => void;
  open: boolean;
  orderId: string | number;
  callback: () => void;
};

const CancelOrderSheet: React.FC<CancelReasonSheetType> = ({
  toggleSheet,
  open,
  orderId,
  callback,
}) => {
  const [loading, setLoading] = useState(false);
  const { account } = useSmartWallet();
  const { refreshOrders } = useOrders();
  const { mutate: cancelOrderTransaction } = useSendTransaction();
  const handleCancelOrder = async () => {
    setLoading(true);
    try {
      const transaction = await customPrepareContractCall({
        contract: orderProcessorContract,
        method: "cancelOrder",
        params: [orderId],
        address: account.address,
      });

      cancelOrderTransaction(transaction as any, {
        onSuccess: () => {
          setLoading(false);
          toast({
            title: `Order #${orderId} has been successfully canceled.`,
          });
          callback();
        },
        onError: (error) => {
          setLoading(false);
          toast({
            title: `Error: ${error.message}`,
            variant: "destructive",
          });
          console.error(error);
        },
      });
      refreshOrders();
    } catch (error: any) {
      console.log({ error });
    }
  };

  return (
    <Drawer open={open}>
      <DrawerContent>
        <div className="rounded px-[16px] pb-[16px]">
          <div className="flex justify-center border-b-[1px] border-stroke-grey py-[14px]">
            <p className="font-satoshiBold text-center text-[16px] tracking-wide text-text-light">
              Cancel Order
            </p>
          </div>
          <p className="font-satoshiRegular my-[16px] text-[16px] tracking-wide text-text-light">
            Are you sure want to cancel the order?
          </p>
          <div className="mt-[16px]">
            <Button
              label={"Continue"}
              loading={loading}
              onClick={handleCancelOrder}
              type={ButtonType.secondary}
              size={ButtonSize.medium}
              widthFull
            />
          </div>
          <div className="mt-[12px]">
            <Button
              label={"Close"}
              onClick={toggleSheet}
              type={ButtonType.primary}
              size={ButtonSize.medium}
              widthFull
            />
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
};

type CancelOrderType = {
  orderId: string | number;
  callback: () => void;
  size?: ButtonSize;
};

export const CancelOrder: React.FC<CancelOrderType> = ({
  orderId,
  callback,
  size = ButtonSize.small,
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const toggleSheet = () => setOpen(!open);
  return (
    <div>
      <CancelOrderSheet
        toggleSheet={toggleSheet}
        open={open}
        orderId={orderId}
        callback={callback}
      />
      <Button
        label={"Cancel"}
        onClick={() => {
          trackAmplitudeEvent("CANCEL_ORDER_CLICKED");
          toggleSheet();
        }}
        type={ButtonType.danger}
        size={size}
        widthFull
      />
    </div>
  );
};
